// @ts-check
// =============================================================================
// js/render/art-preload.js — warm the artwork a duel will need BEFORE it's
// asked for on the battlefield.
//
// WHY (live testing): playing a creature mounts a board tile whose art is
// fetched at that moment — on a cold cache the tile sat empty while the
// flight animation already ran. The player KNOWS every artwork on their own
// side (their hand, their deck list, their hero's egg art), so all of it is
// fetched in the background the moment the duel mounts. The generator keeps
// fetched art in its `arts` blob cache (PixiGetArtTexture), which every
// PixiCard/PixiTile render checks first — a warmed file mounts instantly.
//
// SCOPE: OWN side only, by design. The opponent's cosmetic choices arrive
// with their plays, and waiting on those downloads is acceptable.
//
// ORDER: the hero's egg art first (every played creature becomes it), then
// the hand (playable RIGHT NOW), then the board (a reconnect mid-duel), then
// the full deck list. Three fetches in flight at a time.
// =============================================================================

import { gen, resolveCardArt } from './card-tile.js';

/**
 * Hero index -> egg art number. MIRROR of the server's MakeEgg table
 * (src/engine/cards.js) — the egg a played creature becomes uses
 * "0000_<number>_MinaHyena_generic.jpg" for the OWNER's hero.
 */
const HERO_EGG_NUMBERS = [
	'0002', '0003', '0004', '0005', '0006', '0007', '0008', '0009',
	'0010', '0011', '0012', '0002', '0014', '0015', '0016', '0017', '0018',
];

/** Art files already warmed (or in flight) this session. */
const warmed = new Set();

/**
 * Fetch one art file into the generator's blob cache. Failures un-mark the
 * file so a later render (or the next preload) can retry.
 * @param {string} file  e.g. "0001_0002_Mamabliss_female_female.jpg"
 * @returns {Promise<void>}
 */
export async function warmArtFile(file) {
	if (!file || warmed.has(file)) return;
	warmed.add(file);
	try {
		// PixiGetArtTexture fetches + caches the blob under arts[file]; the
		// sprite wrapper is scaffolding — destroy it, the texture stays cached.
		const sprite = await gen.PixiGetArtTexture({ selectedArt: file, name: 'preload' });
		sprite?.destroy?.();
	} catch {
		warmed.delete(file);
	}
}

/**
 * What art file would this card instance SHOW in the duel? Runs the exact
 * same resolution the renderer runs (strict, deterministic), on a clone so
 * the real state object isn't touched.
 * @param {any} cardLike  Anything with {id, selectedArt?}.
 * @returns {string|undefined}
 */
function duelArtFor(cardLike) {
	if (!cardLike || cardLike.id == null) return undefined;
	const clone = { id: cardLike.id, selectedArt: cardLike.selectedArt };
	resolveCardArt(clone, true);
	return clone.selectedArt;
}

/**
 * Warm every artwork OUR side of this duel can put on the battlefield:
 * hand cards, our hero's egg art (any creature played becomes that egg),
 * our mounted board cards + the true forms under our eggs, and the full
 * loaded deck list. Fire-and-forget from the duel screen's mount.
 * @param {any} duel        The current duel (viewer-oriented: player1 = us).
 * @param {any} [loadedDeck] store.loadedDeck (the deck we queued up with).
 * @returns {Promise<void>}
 */
export async function preloadOwnDuelArts(duel, loadedDeck) {
	if (!duel?.player1) return;
	/** @type {string[]} */
	const files = [];
	const add = (file) => {
		if (file && !files.includes(file)) files.push(file);
	};

	// 1. The egg every played creature becomes (ONE small file per hero) —
	//    first in line: it's needed the moment any creature is played, and
	//    nothing else ever fetches it ahead of time.
	const eggNumber = HERO_EGG_NUMBERS[Number(duel.player1.hero)] || HERO_EGG_NUMBERS[0];
	add(`0000_${eggNumber}_MinaHyena_generic.jpg`);
	// 2. The hand — playable this very turn.
	if (Array.isArray(duel.player1.hand)) {
		for (const card of duel.player1.hand) {
			add(duelArtFor(card));
		}
	}
	// 3. Our side of the board (mid-duel reconnects) + hatch reveals.
	for (const row of duel.board || []) {
		for (const cell of row || []) {
			if (!cell || cell.owner !== duel.player1.id) continue;
			add(duelArtFor(cell));
			if (cell.trueForm && cell.trueForm !== true) {
				add(duelArtFor(cell.trueForm));
			}
		}
	}
	// 4. The rest of the deck — draws land pre-warmed.
	if (Array.isArray(loadedDeck?.list)) {
		for (const entry of loadedDeck.list) {
			add(duelArtFor(entry));
		}
	}

	// Three fetches in flight at a time: fast enough that the whole deck is
	// warm within the first turns even on slow links, gentle enough not to
	// starve the duel's own traffic.
	const queue = [...files];
	await Promise.all(Array.from({ length: 3 }, async () => {
		while (queue.length) {
			await warmArtFile(queue.shift());
		}
	}));
}
